<script setup lang="ts">
import { computed, useId } from 'vue'
import LockedOption from '@/components/LockedOption.vue'
import type { FacetOption } from '@/browse/browse_state'
import { facetOptionStateClasses, purpleFacetOptionStateClasses } from '@/browse/facet_option_classes'

export type RadioFacetTheme = 'blue' | 'purple'

type ThemeClasses = {
  optionClasses: (option: FacetOption) => string
  labelClass: string
  lockedClass: string
  inputClass: string
}

// Purple is Present Levels' category list: no radio dot, just a pill around the chosen option.
// Its radios stay in the page, visually hidden, so the keyboard and screen readers still use them.
const THEME_CLASSES: Record<RadioFacetTheme, ThemeClasses> = {
  blue: { optionClasses: facetOptionStateClasses, labelClass: 'gap-2 px-2 py-1 leading-5', lockedClass: 'px-2 py-1 leading-5', inputClass: 'accent-blue-400' },
  purple: {
    optionClasses: purpleFacetOptionStateClasses,
    labelClass: 'px-3.5 py-1.5 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-blue-400',
    lockedClass: 'px-2 py-0.5',
    inputClass: 'sr-only'
  }
}

const props = withDefaults(
  defineProps<{ options: FacetOption[], labelledBy: string, theme?: RadioFacetTheme, hidesUnavailableRadios?: boolean }>(),
  { theme: 'blue', hidesUnavailableRadios: false }
)

const emit = defineEmits<{ toggle: [value: string] }>()

const groupName = useId()
const themeClasses = computed(() => THEME_CLASSES[props.theme])
</script>

<template>
  <!-- Listens to click rather than change, so choosing the selected option again clears it -->
  <div role="radiogroup" :aria-labelledby="labelledBy">
    <ul :aria-labelledby="labelledBy" class="space-y-1">
      <li v-for="option in options" :key="option.value">
        <LockedOption v-if="option.isLocked" :label="option.label" class="cursor-not-allowed text-text-md" :class="themeClasses.lockedClass" />
        <label
          v-else
          class="flex items-center rounded-lg text-text-md"
          :class="[themeClasses.labelClass, themeClasses.optionClasses(option)]"
        >
          <input
            type="radio"
            :class="[themeClasses.inputClass, { invisible: hidesUnavailableRadios && !option.isEnabled }]"
            :name="groupName"
            :checked="option.isSelected"
            :disabled="!option.isEnabled"
            @click="emit('toggle', option.value)"
          >
          {{ option.label }}
        </label>
      </li>
    </ul>
  </div>
</template>
